import { Controller } from "@hotwired/stimulus"
import { resolveStep, isValidKindleEmail } from "../lib/wizard_steps"

// Onboarding step wizard. Shows one panel at a time, backs the current step
// with the URL (history.pushState — no native visit proposal on iOS, ignored
// by Turbo because our state entries carry no `turbo` key), gates the CTA per
// panel, slides between panels, and mounts the native left bar-button (back to
// the welcome fork on the intro, in-wizard back chevron thereafter) into a
// slot from <template> elements.
const TRACKED_STEPS_KEY = "dropkind.wizard.trackedSteps"

export default class extends Controller {
  static targets = [
    "panel", "cta", "fill", "back", "form",
    "slot", "welcomeTemplate", "chevronTemplate", "emailInput", "emailHint"
  ]
  static values = { initialStep: Number }

  connect() {
    this.currentSlotKind = null
    this.animationAbort = null

    const queryStep = new URLSearchParams(window.location.search).get("step")
    this.step = resolveStep(this.initialStepValue, queryStep, this.emailValid)

    // Rebuild the history chain beneath the current entry, so history.back()
    // (chevron and browser/native back alike) walks the full path even when
    // the wizard was entered mid-flow — a deep link, a 422 re-render, or a
    // completed user stepping back from the success screen. On a step-0 load
    // this is exactly one replaceState annotating the current entry.
    history.replaceState({ step: 0 }, "", this.urlFor(0))
    for (let i = 1; i <= this.step; i++) {
      history.pushState({ step: i }, "", this.urlFor(i))
    }

    this.onPopstateBound = this.onPopstate.bind(this)
    window.addEventListener("popstate", this.onPopstateBound)

    this.render()
    this.trackStep()
  }

  disconnect() {
    window.removeEventListener("popstate", this.onPopstateBound)
    this.animationAbort?.abort()
  }

  // --- Navigation ----------------------------------------------------------

  next() {
    // onSubmit already guards the gate; this is a belt-and-suspenders check
    // for any future direct callers.
    if (!this.gateSatisfied(this.activePanel)) return
    this.goTo(this.step + 1)
  }

  back() {
    if (this.step === 0) return
    // popstate drives the transition; keeps the URL and history in step.
    history.back()
  }

  goTo(n) {
    const clamped = Math.max(0, Math.min(n, this.panelTargets.length - 1))
    history.pushState({ step: clamped }, "", this.urlFor(clamped))
    this.transitionTo(clamped)
  }

  onPopstate(event) {
    // Only react to our own entries; ignore foreign/Turbo state.
    if (event.state && Number.isInteger(event.state.step)) {
      this.transitionTo(event.state.step)
    }
  }

  urlFor(step) {
    return step === 0 ? window.location.pathname : `${window.location.pathname}?step=${step}`
  }

  // --- Transitions ---------------------------------------------------------

  transitionTo(n) {
    const previous = this.step
    if (n === previous) {
      this.render()
      this.trackStep()
      return
    }

    const forward = n > previous
    const outgoing = this.panelTargets[previous]
    const incoming = this.panelTargets[n]

    // Clear any classes left over from an animation interrupted by a rapid
    // transition so they never stack.
    this.cleanupPanels()
    this.step = n

    if (this.prefersReducedMotion || !outgoing || !incoming) {
      // Snap without sliding; render() sets final visibility.
      window.scrollTo(0, 0)
      this.render()
      this.trackStep()
      return
    }

    // One AbortController per transition: cleanupPanels() aborts it so listeners
    // from an interrupted slide can't fire on the next one.
    this.animationAbort = new AbortController()
    const { signal } = this.animationAbort

    outgoing.classList.remove("is-active")
    outgoing.classList.add("is-exiting", forward ? "wizard-exit-fwd" : "wizard-exit-back")
    this.onAnimationEnd(outgoing, signal, () => {
      outgoing.classList.remove("is-exiting", "wizard-exit-fwd", "wizard-exit-back")
      outgoing.setAttribute("hidden", "")
    })

    incoming.removeAttribute("hidden")
    incoming.classList.add("is-active", forward ? "wizard-enter-fwd" : "wizard-enter-back")
    this.onAnimationEnd(incoming, signal, () => {
      incoming.classList.remove("wizard-enter-fwd", "wizard-enter-back")
    })

    window.scrollTo(0, 0)
    this.render()
    this.trackStep()
  }

  cleanupPanels() {
    // Detach any pending listeners BEFORE stripping classes, so the
    // animationcancel this triggers on the interrupted panels goes nowhere.
    this.animationAbort?.abort()
    this.animationAbort = null

    this.panelTargets.forEach((panel) => {
      panel.classList.remove(
        "is-exiting", "wizard-exit-fwd", "wizard-exit-back", "wizard-enter-fwd", "wizard-enter-back"
      )
    })
  }

  onAnimationEnd(panel, signal, callback) {
    const handler = (event) => {
      if (event.target !== panel) return
      callback()
    }
    // { signal } auto-removes both listeners when the transition's controller
    // aborts. animationcancel covers a class stripped mid-flight or a
    // reduced-motion animation resolved to none.
    panel.addEventListener("animationend", handler, { signal })
    panel.addEventListener("animationcancel", handler, { signal })
  }

  // --- Gates ---------------------------------------------------------------

  gateSatisfied(panel) {
    if (!panel) return true

    switch (panel.dataset.wizardGate) {
      case "email":
        return this.hasEmailInputTarget && isValidKindleEmail(this.emailInputTarget.value)
      default:
        return true
    }
  }

  emailChanged() {
    this.updateEmailHint()
    this.render()
  }

  markAmazonOpened() {
    this.trackAction("onboarding_amazon_settings_opened")
  }

  senderCopyRequested() {
    this.trackAction("onboarding_sender_email_copied")
  }

  // Draw the eye to the field blocking the CTA with a zoom-in/out pulse.
  nudgeGate(panel) {
    if (panel?.dataset.wizardGate !== "email" || !this.hasEmailInputTarget) return

    const input = this.emailInputTarget
    input.classList.remove("wizard-nudge")
    // Force a reflow so a re-tap restarts the pulse from the beginning.
    void input.offsetWidth
    input.classList.add("wizard-nudge")
  }

  // --- Rendering -----------------------------------------------------------

  render() {
    this.panelTargets.forEach((panel, index) => {
      // A panel mid-exit is owned by its animationend handler; leave it be.
      if (panel.classList.contains("is-exiting")) return
      const active = index === this.step
      panel.classList.toggle("is-active", active)
      panel.toggleAttribute("hidden", !active)
    })

    const panel = this.activePanel
    if (!panel) return

    if (this.hasCtaTarget) {
      this.ctaTarget.textContent = panel.dataset.wizardCta
    }

    if (this.hasFillTarget) {
      this.fillTarget.style.width = `${panel.dataset.wizardProgress}%`
    }

    if (this.hasBackTarget) {
      this.backTarget.toggleAttribute("hidden", this.step === 0)
    }

    this.updateSlot()
  }

  updateEmailHint() {
    if (!this.hasEmailHintTarget || !this.hasEmailInputTarget) return

    const value = this.emailInputTarget.value.trim()
    const showInvalid = value !== "" && !isValidKindleEmail(value)

    if (showInvalid) {
      this.emailHintTarget.textContent = this.emailHintTarget.dataset.errorInvalid
      this.emailHintTarget.classList.remove("hidden")
      this.emailInputTarget.setAttribute("aria-invalid", "true")
    } else {
      this.emailHintTarget.classList.add("hidden")
      this.emailHintTarget.textContent = ""
      this.emailInputTarget.removeAttribute("aria-invalid")
    }
  }

  showEmailError() {
    if (!this.hasEmailHintTarget || !this.hasEmailInputTarget) return

    const blank = this.emailInputTarget.value.trim() === ""
    this.emailHintTarget.textContent = blank
      ? this.emailHintTarget.dataset.errorBlank
      : this.emailHintTarget.dataset.errorInvalid
    this.emailHintTarget.classList.remove("hidden")
    this.emailInputTarget.setAttribute("aria-invalid", "true")
  }

  updateSlot() {
    if (!this.hasSlotTarget) return

    const desiredKind = this.step === 0 ? "welcome" : "chevron"
    // Re-cloning every render would churn the native button; only swap on change.
    if (desiredKind === this.currentSlotKind) return

    const template = desiredKind === "welcome"
      ? (this.hasWelcomeTemplateTarget ? this.welcomeTemplateTarget : null)
      : (this.hasChevronTemplateTarget ? this.chevronTemplateTarget : null)

    if (template) {
      this.slotTarget.replaceChildren(template.content.cloneNode(true))
    } else {
      this.slotTarget.replaceChildren()
    }
    this.currentSlotKind = desiredKind
  }

  // --- Submit --------------------------------------------------------------

  onSubmit(event) {
    const panel = this.activePanel

    // Button taps and Enter-key submits from the email field both land here
    // while the gate is unsatisfied.
    if (!this.gateSatisfied(panel)) {
      event.preventDefault()
      this.nudgeGate(panel)
      if (panel?.dataset.wizardGate === "email" && this.hasEmailInputTarget) {
        this.showEmailError()
        this.emailInputTarget.focus()
      }
      return
    }

    // Only the final panel submits the form; every other CTA advances a step.
    if (!panel?.hasAttribute("data-wizard-submit")) {
      event.preventDefault()
      this.next()
    } else {
      this.trackAction("onboarding_setup_save_and_test_submitted")
    }
    // Otherwise let the native submit proceed (never call form.submit()).
  }

  // --- Analytics -----------------------------------------------------------

  // Fire a GA4 view event for the active step, at most once per browser
  // session. Mirrors analytics_controller.js: bail when gtag is absent
  // (test/dev have none), so a gtag-less page can never break here. Dedupe
  // lives in sessionStorage, so it survives the marketplace-change reload but
  // resets on a fresh session; back-navigation to a seen step is a no-op.
  trackStep() {
    if (typeof window.gtag !== "function") return

    const stepName = this.activePanel?.dataset.wizardStep
    if (!stepName) return

    const tracked = this.trackedSteps
    if (tracked.has(stepName)) return

    window.gtag("event", "onboarding_step_view", { step_name: stepName })

    tracked.add(stepName)
    this.persistTrackedSteps(tracked)
  }

  trackAction(action) {
    if (typeof window.gtag === "function") {
      window.gtag("event", action)
    }
  }

  get trackedSteps() {
    try {
      const raw = sessionStorage.getItem(TRACKED_STEPS_KEY)
      const list = raw ? JSON.parse(raw) : []
      return new Set(Array.isArray(list) ? list : [])
    } catch {
      // Storage unavailable or corrupt: no dedupe memory, so events re-fire.
      // Double-counting a step beats bricking the wizard.
      return new Set()
    }
  }

  persistTrackedSteps(set) {
    try {
      sessionStorage.setItem(TRACKED_STEPS_KEY, JSON.stringify([...set]))
    } catch {
      // Private-mode Safari throws; drop the dedupe memory silently.
    }
  }

  // --- Helpers -------------------------------------------------------------

  get activePanel() {
    return this.panelTargets[this.step]
  }

  get emailValid() {
    return this.hasEmailInputTarget && isValidKindleEmail(this.emailInputTarget.value)
  }

  get prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
